import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import currencyJs from "currency.js";

export type CurrencyCode = "USD" | "EUR" | "GBP" | "PLN";

export const AVAILABLE_CURRENCIES: ReadonlyArray<CurrencyCode> = [
  "USD",
  "EUR", 
  "GBP",
  "PLN",
] as const;

const CURRENCY_SYMBOL: Record<CurrencyCode, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  PLN: "zł",
};

const CURRENCY_FLAG: Record<CurrencyCode, string> = {
  USD: "🇺🇸",
  EUR: "🇪🇺", 
  GBP: "🇬🇧",
  PLN: "🇵🇱",
};

const CURRENCY_LOCALE: Record<CurrencyCode, string> = {
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB", 
  PLN: "pl-PL",
};

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (currency: CurrencyCode) => void;
  symbol: string;
  flag: string;
  format: (amount: number, opts?: { currency?: CurrencyCode; locale?: string; showSymbol?: boolean; showFlag?: boolean }) => string;
  formatWithOriginal: (amount: number, originalCurrency: CurrencyCode, originalAmount: number) => string;
  convert: (amount: number, from?: CurrencyCode, to?: CurrencyCode) => number;
}

const STORAGE_KEY = "app:currency";
const RATES_KEY = "app:currency:rates";

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    const saved = (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) as CurrencyCode | null;
    if (saved && (AVAILABLE_CURRENCIES as readonly string[]).includes(saved)) return saved as CurrencyCode;
    return "USD";
  });

  const setCurrency = (next: CurrencyCode) => {
    setCurrencyState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    // Persist to user profile if logged in (fire-and-forget)
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        await supabase.from('profiles').update({ preferred_currency: next } as any).eq('user_id', user.id);
      } catch {}
    })();
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data, error } = await supabase
          .from('profiles')
          .select('preferred_currency' as any)
          .eq('user_id', user.id)
          .maybeSingle();
        const pref = (data as any)?.preferred_currency as string | undefined;
        if (!error && pref && (AVAILABLE_CURRENCIES as readonly string[]).includes(pref)) {
          if (isMounted) setCurrencyState(pref as CurrencyCode);
        }
      } catch {}
    })();
    return () => { isMounted = false; };
  }, []);

  const format = (amount: number, opts?: { currency?: CurrencyCode; locale?: string; showSymbol?: boolean; showFlag?: boolean }) => {
    const code = opts?.currency ?? currency;
    const locale = opts?.locale ?? CURRENCY_LOCALE[code];
    const showSymbol = opts?.showSymbol !== false;
    const showFlag = opts?.showFlag === true;
    
    try {
      const formatted = currencyJs(amount, {
        symbol: showSymbol ? CURRENCY_SYMBOL[code] : '',
        precision: 2,
        pattern: code === 'PLN' ? '# !!' : '!#',
        negativePattern: code === 'PLN' ? '-# !!' : '-!#',
      }).format();
      
      return showFlag ? `${CURRENCY_FLAG[code]} ${formatted}` : formatted;
    } catch {
      // Fallback simple formatting
      const flag = showFlag ? `${CURRENCY_FLAG[code]} ` : '';
      const symbol = showSymbol ? CURRENCY_SYMBOL[code] : '';
      return `${flag}${symbol}${amount.toFixed(2)}`;
    }
  };

  const formatWithOriginal = (amount: number, originalCurrency: CurrencyCode, originalAmount: number) => {
    const convertedFormatted = format(amount, { showFlag: true });
    if (originalCurrency === currency) {
      return convertedFormatted;
    }
    const originalFormatted = format(originalAmount, { currency: originalCurrency, showSymbol: true });
    return `${convertedFormatted} (${originalFormatted})`;
  };

  const [rates, setRates] = useState<Record<CurrencyCode, number>>(() => {
    try {
      const cached = localStorage.getItem(RATES_KEY);
      if (cached) return JSON.parse(cached);
    } catch {}
    return { USD: 1, EUR: 0.92, GBP: 0.78, PLN: 4.0 };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-exchange-rates', { body: {} });
        if (error) throw error;
        const json = data as { rates?: Record<string, number> };
        const next: Record<CurrencyCode, number> = {
          USD: json?.rates?.USD ?? 1,
          EUR: json?.rates?.EUR ?? rates.EUR,
          GBP: json?.rates?.GBP ?? rates.GBP,
          PLN: json?.rates?.PLN ?? rates.PLN,
        };
        if (!cancelled) {
          setRates(next);
          try { localStorage.setItem(RATES_KEY, JSON.stringify(next)); } catch {}
        }
      } catch {
        // ignore; keep cached/default
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const convert = (amount: number, from: CurrencyCode = "USD", to: CurrencyCode = currency) => {
    if (from === to) return amount;
    const toUsd = amount / (rates[from] || 1);
    return toUsd * (rates[to] || 1);
  };

  const value = useMemo<CurrencyContextType>(() => ({
    currency,
    setCurrency,
    symbol: CURRENCY_SYMBOL[currency],
    flag: CURRENCY_FLAG[currency],
    format,
    formatWithOriginal,
    convert,
  }), [currency, rates]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within a CurrencyProvider");
  return ctx;
};
